type ImageCdnResolveInput = string | string[];

export type ImageCacheMode = 'rewrite' | 'controller';

export interface ImageCdnResolveOptions {
    format?: string;
    quality?: string | number;
    width?: string | number;
    version?: string | number | boolean;

    /**
     * rewrite：輸出圖片 URL + format / quality，交給 IIS Rewrite 處理
     * controller：符合 Rewrite path pattern 時，直接輸出 /Image/GetCache
     */
    imageCacheMode?: ImageCacheMode;
    imageCacheControllerPath?: string;
    imageCacheControllerPathPatterns?: string[];
}

const DEFAULT_IMAGE_CACHE_MODE: ImageCacheMode = 'rewrite';
const DEFAULT_IMAGE_CACHE_CONTROLLER_PATH = '/Image/GetCache';

/**
 * 對齊 IIS Rewrite：
 * <match url="^(Images/.+|Content/doc/.+)\.(png|jpg|jpeg)$" />
 */
const DEFAULT_IMAGE_CACHE_CONTROLLER_PATH_PATTERNS = [
    '^(Images/.+|Content/doc/.+)\\.(png|jpg|jpeg)$',
];

/**
 * 依 CDN prefix 與 cache 策略產生圖片 URL 候選清單
 */
export class ImageCdnUrlResolver {
    private readonly cdnPrefixes: string[];

    private readonly cache: Map<string, string[]> | null;

    constructor(cdnPrefixes: string[] = [], enableCache = true) {
        this.cdnPrefixes = this.normalizeCdnPrefixes(cdnPrefixes);
        this.cache = enableCache ? new Map<string, string[]>() : null;
    }

    private normalizeCdnPrefixes(cdnPrefixes: string[]): string[] {
        if (!Array.isArray(cdnPrefixes) || !cdnPrefixes.length) {
            return ['/'];
        }

        const normalized = cdnPrefixes
            .map((prefix) => String(prefix || '').trim())
            .filter((prefix) => Boolean(prefix));

        return normalized.length ? Array.from(new Set(normalized)) : ['/'];
    }

    private isAbsoluteUrl(path: string): boolean {
        return /^https?:\/\//i.test(path) || /^\/\//.test(path);
    }

    private isDirectImageUrl(path: string): boolean {
        return /^(data:image\/|blob:)/i.test(path);
    }

    private normalizeImagePath(path: string): string {
        if (!path || typeof path !== 'string') return '';

        const normalizedPath = path.trim().replace(/^(['"])(.+)\1$/, '$2');

        if (
            this.isDirectImageUrl(normalizedPath) ||
            this.isAbsoluteUrl(normalizedPath)
        ) {
            return normalizedPath;
        }

        return normalizedPath
            .replace(/^(\.\.\/|\.\/)+/, '')
            .replace(/\/{2,}/g, '/');
    }

    private getSafeUrl(urlValue: string): URL {
        return new URL(
            /^\/\//.test(urlValue)
                ? `${window.location.protocol}${urlValue}`
                : urlValue,
            window.location.origin
        );
    }

    private appendImageCacheParams(
        urlValue: string,
        options: ImageCdnResolveOptions
    ): string {
        const url = this.getSafeUrl(urlValue);

        const originalFormat = String(url.searchParams.get('format') || '')
            .trim()
            .toLowerCase();

        const requestedFormat = String(options.format || '')
            .trim()
            .toLowerCase();

        const effectiveFormat = originalFormat || requestedFormat;

        // 原 URL 已有 format 時，不覆蓋
        if (requestedFormat && !url.searchParams.has('format')) {
            url.searchParams.set('format', requestedFormat);
        }

        // 只有 URL 原本有 format 或 options 有 format 時，才補 quality
        const quality = options.quality;
        if (
            effectiveFormat &&
            quality !== undefined &&
            quality !== null &&
            String(quality) !== '' &&
            !url.searchParams.has('quality')
        ) {
            url.searchParams.set('quality', String(quality));
        }

        const version = options.version;
        if (
            version !== undefined &&
            version !== null &&
            version !== '' &&
            version !== false &&
            !url.searchParams.has('v')
        ) {
            url.searchParams.set(
                'v',
                version === true ? String(Date.now()) : String(version)
            );
        }

        // width > 0 時附加 width，讓 IIS 服務 (或 Image controller 產生) width 修正後的 webp
        const width = options.width;
        if (
            width !== undefined &&
            width !== null &&
            String(width) !== '' &&
            Number(width) > 0 &&
            !url.searchParams.has('width')
        ) {
            url.searchParams.set('width', String(Number(width)));
        }

        return url.origin === window.location.origin
            ? `${url.pathname}${url.search}${url.hash}`
            : url.href;
    }

    /**
     * 只套用 cdnPrefix / base path，不加 format / quality / version
     * controller mode 要先得到實際路徑後，再決定是否改走 /Image/GetCache
     */
    private buildUrl(prefix: string, imgPath: string): string {
        if (!imgPath) return '';

        if (this.isAbsoluteUrl(imgPath)) {
            const url = this.getSafeUrl(imgPath);
            return url.origin === window.location.origin
                ? `${url.pathname}${url.search}${url.hash}`
                : url.href;
        }

        const rawPrefix = String(prefix || '/').trim() || '/';
        const cleanPath = imgPath.replace(/^\/+/, '');

        if (this.isAbsoluteUrl(rawPrefix)) {
            const prefixUrl = this.getSafeUrl(rawPrefix);
            const cleanPrefixPath = prefixUrl.pathname
                .replace(/^\/+/, '')
                .replace(/\/+$/, '');

            const finalPath =
                cleanPrefixPath &&
                cleanPath
                    .toLowerCase()
                    .startsWith(`${cleanPrefixPath.toLowerCase()}/`)
                    ? cleanPath
                    : cleanPrefixPath
                      ? `${cleanPrefixPath}/${cleanPath}`
                      : cleanPath;

            const url = new URL(`/${finalPath}`, prefixUrl.origin);
            return url.origin === window.location.origin
                ? `${url.pathname}${url.search}${url.hash}`
                : url.href;
        }

        const cleanPrefix = rawPrefix.replace(/^\/+/, '').replace(/\/+$/, '');

        const finalPath =
            cleanPrefix &&
            cleanPath.toLowerCase().startsWith(`${cleanPrefix.toLowerCase()}/`)
                ? cleanPath
                : cleanPrefix
                  ? `${cleanPrefix}/${cleanPath}`
                  : cleanPath;

        const url = new URL(`/${finalPath}`, window.location.origin);
        return `${url.pathname}${url.search}${url.hash}`;
    }

    private getControllerPathPatterns(
        options: ImageCdnResolveOptions
    ): string[] {
        return Array.isArray(options.imageCacheControllerPathPatterns) &&
            options.imageCacheControllerPathPatterns.length
            ? options.imageCacheControllerPathPatterns
            : DEFAULT_IMAGE_CACHE_CONTROLLER_PATH_PATTERNS;
    }

    private getPathForPatternMatch(urlValue: string): string {
        const url = this.getSafeUrl(urlValue);
        return url.pathname.replace(/^\/+/, '');
    }

    private canUseControllerMode(
        urlValue: string,
        options: ImageCdnResolveOptions
    ): boolean {
        if (
            (options.imageCacheMode || DEFAULT_IMAGE_CACHE_MODE) !==
            'controller'
        ) {
            return false;
        }

        const url = this.getSafeUrl(urlValue);

        // 外部 CDN 不改打本機 Controller，避免所有 CDN 都進後端
        if (url.origin !== window.location.origin) return false;

        const effectiveFormat =
            String(url.searchParams.get('format') || '').trim() ||
            String(options.format || '').trim();

        if (!effectiveFormat) return false;

        const path = this.getPathForPatternMatch(urlValue);
        const patterns = this.getControllerPathPatterns(options);

        // controller mode 只處理與 IIS Rewrite 相同的圖片範圍
        return patterns.some((pattern) => {
            try {
                return new RegExp(pattern, 'i').test(path);
            } catch {
                return false;
            }
        });
    }

    private buildControllerUrl(
        imageUrl: string,
        options: ImageCdnResolveOptions
    ): string {
        const sourceUrl = this.getSafeUrl(imageUrl);
        const controllerPath =
            options.imageCacheControllerPath ||
            DEFAULT_IMAGE_CACHE_CONTROLLER_PATH;

        const url = new URL(controllerPath, window.location.origin);
        const queryParts = [
            `file=${encodeURI(sourceUrl.pathname.replace(/^\/+/, ''))}`,
        ];

        const format =
            String(sourceUrl.searchParams.get('format') || '').trim() ||
            String(options.format || '').trim();

        if (format) {
            queryParts.push(`format=${encodeURIComponent(format)}`);
        }

        const quality =
            sourceUrl.searchParams.get('quality') ||
            (options.quality !== undefined &&
            options.quality !== null &&
            String(options.quality) !== ''
                ? String(options.quality)
                : '');

        if (format && quality) {
            queryParts.push(`quality=${encodeURIComponent(quality)}`);
        }

        const version =
            sourceUrl.searchParams.get('v') ||
            (options.version !== undefined &&
            options.version !== null &&
            options.version !== '' &&
            options.version !== false
                ? options.version === true
                    ? String(Date.now())
                    : String(options.version)
                : '');

        if (version) {
            queryParts.push(`v=${encodeURIComponent(String(version))}`);
        }

        const width =
            sourceUrl.searchParams.get('width') ||
            (options.width !== undefined &&
            options.width !== null &&
            String(options.width) !== '' &&
            Number(options.width) > 0
                ? String(Number(options.width))
                : '');

        if (width) {
            queryParts.push(`width=${encodeURIComponent(width)}`);
        }

        return `${url.pathname}?${queryParts.join('&')}`;
    }

    private buildCacheKey(
        imgPath: string,
        options: ImageCdnResolveOptions
    ): string {
        return JSON.stringify({
            imgPath,
            format: options.format || '',
            quality: options.quality || '',
            version: options.version || '',
            width: options.width || '',
            imageCacheMode: options.imageCacheMode || DEFAULT_IMAGE_CACHE_MODE,
            imageCacheControllerPath:
                options.imageCacheControllerPath ||
                DEFAULT_IMAGE_CACHE_CONTROLLER_PATH,
            imageCacheControllerPathPatterns:
                this.getControllerPathPatterns(options),
            cdnPrefixes: this.cdnPrefixes,
        });
    }

    resolveCandidates(
        imgPath: string,
        options: ImageCdnResolveOptions = {}
    ): string[] {
        const cleanPath = this.normalizeImagePath(imgPath);
        if (!cleanPath) return [];

        const cacheKey = this.buildCacheKey(cleanPath, options);
        const cached = this.cache?.get(cacheKey);
        if (cached) return cached.slice();

        if (this.isDirectImageUrl(cleanPath)) {
            return [cleanPath];
        }

        const baseCandidates = this.isAbsoluteUrl(cleanPath)
            ? [this.buildUrl('/', cleanPath)]
            : this.cdnPrefixes.map((prefix) =>
                  this.buildUrl(prefix, cleanPath)
              );

        const candidates = baseCandidates
            .map((candidate) => {
                const candidateWithParams = this.appendImageCacheParams(
                    candidate,
                    options
                );

                if (this.canUseControllerMode(candidateWithParams, options)) {
                    return this.buildControllerUrl(
                        candidateWithParams,
                        options
                    );
                }

                return candidateWithParams;
            })
            .filter((url, index, list) => {
                return Boolean(url) && list.indexOf(url) === index;
            });

        this.cache?.set(cacheKey, candidates);
        return candidates.slice();
    }

    resolveFirst(
        imgPath: string,
        options: ImageCdnResolveOptions = {}
    ): string {
        return this.resolveCandidates(imgPath, options)[0] || '';
    }

    resolve(
        input: ImageCdnResolveInput,
        options: ImageCdnResolveOptions = {}
    ): string | string[] {
        if (!Array.isArray(input)) {
            return this.resolveFirst(input, options);
        }

        return input
            .map((item) => this.resolveFirst(item, options))
            .filter((item) => Boolean(item));
    }
}
